import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { collection, query, where, getDocs, orderBy, doc, onSnapshot } from 'firebase/firestore';
import { db } from '../firebase';
import { useBooks } from '../context/BooksContext';
import { useUser } from '../context/UserContext';
import SEO from '../components/SEO';
import { useSocial } from '../context/SocialContext';
import TipAuthor from '../components/TipAuthor';
import { IoArrowBack, IoBook, IoHeart, IoEye, IoPeople, IoPersonAdd, IoPersonRemove, IoChatbubble, IoStar, IoShield, IoFlame, IoRocket, IoTrophy, IoNewspaper } from 'react-icons/io5';
import './AuthorProfile.css';

const ACCOUNT_BADGES = {
  admin: { label: 'Admin', icon: IoShield, color: '#c27a7a' },
  moderator: { label: 'Modérateur', icon: IoShield, color: '#7a9ab5' },
  beta: { label: 'Bêta Testeur', icon: IoFlame, color: '#f59e0b' },
  author_verified: { label: 'Auteur Vérifié', icon: IoStar, color: '#7a9e7e' },
};

const BADGE_DEFINITIONS = [
  { id: 'first_read', emoji: '📖', name: 'Premier Pas', description: 'Ajouter un livre à sa bibliothèque' },
  { id: 'bookworm', emoji: '🐛', name: 'Rat de Bibliothèque', description: '5 livres en bibliothèque' },
  { id: 'first_comment', emoji: '💬', name: 'Première Voix', description: 'Poster un commentaire' },
  { id: 'author', emoji: '✍️', name: 'Auteur', description: 'Publier un livre' },
  { id: 'prolific', emoji: '📚', name: 'Prolifique', description: 'Publier 3 livres' },
  { id: 'popular', emoji: '🌟', name: 'Étoile Montante', description: '10 likes reçus' },
  { id: 'social', emoji: '🤝', name: 'Social', description: 'Suivre 3 auteurs' },
  { id: 'influencer', emoji: '👑', name: 'Influenceur', description: '5 followers' },
  { id: 'explorer', emoji: '🧭', name: 'Explorateur', description: '3 genres différents lus' },
  { id: 'swiper', emoji: '👆', name: 'Swipeur Pro', description: '20 livres swipés' },
];

function timeAgo(d) {
  if (!d) return '';
  const date = d.toDate ? d.toDate() : new Date(d);
  const s = (Date.now() - date.getTime()) / 1000;
  if (s < 60) return "À l'instant";
  if (s < 3600) return Math.floor(s / 60) + ' min';
  if (s < 86400) return Math.floor(s / 3600) + ' h';
  if (s < 604800) return Math.floor(s / 86400) + ' j';
  return date.toLocaleDateString('fr-FR');
}

export default function AuthorProfile() {
  const { authorId } = useParams();
  const navigate = useNavigate();
  const { books } = useBooks();
  const { user } = useUser();
  const { isFollowing, followUser, unfollowUser, getFollowerCount } = useSocial();

  const [authorInfo, setAuthorInfo] = useState(null);
  const [followerCount, setFollowerCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [authorUserId, setAuthorUserId] = useState(null);
  const [tab, setTab] = useState('livres');
  const [posts, setPosts] = useState([]);
  const [authorBadges, setAuthorBadges] = useState([]);

  const authorBooks = books.filter(b => b.authorId === authorId && b.status !== 'draft');
  const totalLikes = authorBooks.reduce((sum, b) => sum + (b.likes || 0), 0);
  const totalViews = authorBooks.reduce((sum, b) => sum + (b.views || 0), 0);

  useEffect(() => {
    const fetchAuthor = async () => {
      const q = query(collection(db, 'users'), where('authorId', '==', authorId));
      const snapshot = await getDocs(q);
      if (!snapshot.empty) {
        const data = snapshot.docs[0];
        const info = { id: data.id, ...data.data() };
        setAuthorInfo(info);
        setAuthorUserId(data.id);
        const count = await getFollowerCount(data.id);
        setFollowerCount(count);

        // Calculer les badges de l'auteur
        const userBooks = books.filter(b => b.authorId === authorId);
        const earned = [];
        if (userBooks.length >= 1) earned.push('author');
        if (userBooks.length >= 3) earned.push('prolific');
        const likesReceived = userBooks.reduce((s, b) => s + (b.likes || 0), 0);
        if (likesReceived >= 10) earned.push('popular');
        if (count >= 5) earned.push('influencer');

        // Charger les données sociales de l'auteur
        try {
          const socialSnap = await getDocs(query(collection(db, 'social'), where('__name__', '==', data.id)));
          if (!socialSnap.empty) {
            const socialData = socialSnap.docs[0].data();
            if (socialData.following && socialData.following.length >= 3) earned.push('social');
          }
        } catch (e) {}

        // Charger la bibliothèque de l'auteur
        try {
          const libSnap = await getDocs(query(collection(db, 'userLibraries'), where('__name__', '==', data.id)));
          if (!libSnap.empty) {
            const libData = libSnap.docs[0].data();
            if (libData.library && libData.library.length >= 1) earned.push('first_read');
            if (libData.library && libData.library.length >= 5) earned.push('bookworm');
            if (libData.swipedBooks && libData.swipedBooks.length >= 20) earned.push('swiper');
            const genres = new Set((libData.library || []).map(b => b.genre).filter(Boolean));
            if (genres.size >= 3) earned.push('explorer');
          }
        } catch (e) {}

        earned.push('first_comment'); // Simplifié
        setAuthorBadges(earned);
      } else {
        const sampleBook = authorBooks[0];
        if (sampleBook) {
          setAuthorInfo({ name: sampleBook.author, bio: '', authorId });
        }
      }
      setLoading(false);
    };
    fetchAuthor();
  }, [authorId]);

  // Charger les posts de l'auteur
  useEffect(() => {
    if (!authorUserId) return;
    const q = query(collection(db, 'posts'), where('authorId', '==', authorUserId), orderBy('createdAt', 'desc'));
    const unsub = onSnapshot(q, (snap) => {
      setPosts(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    });
    return () => unsub();
  }, [authorUserId]);

  const isOwnProfile = user && authorInfo && authorUserId === user.id;
  const following = authorUserId ? isFollowing(authorUserId) : false;

  const handleFollow = () => {
    if (!authorUserId) return;
    if (following) unfollowUser(authorUserId);
    else followUser(authorUserId);
    setFollowerCount(prev => following ? prev - 1 : prev + 1);
  };

  const getAccountBadges = () => {
    if (!authorInfo) return [];
    const badges = [];
    if (authorInfo.accountType && ACCOUNT_BADGES[authorInfo.accountType]) {
      badges.push(ACCOUNT_BADGES[authorInfo.accountType]);
    }
    if (authorInfo.premium) {
      badges.push({ label: 'Plumlien+', icon: IoRocket, color: '#c8956c' });
    } else {
      badges.push({ label: 'Plumlien', icon: IoPeople, color: '#8b6f8e' });
    }
    return badges;
  };

  if (loading) {
    return <div className="page" style={{ textAlign: 'center', paddingTop: 60 }}>Chargement...</div>;
  }

  if (!authorInfo) {
    return (
      <div className="page">
        <p>Auteur introuvable</p>
        <button className="btn btn-primary" onClick={() => navigate(-1)}>Retour</button>
      </div>
    );
  }

  const accountBadges = getAccountBadges();
  const earnedBadgesDefs = BADGE_DEFINITIONS.filter(b => authorBadges.includes(b.id));

  return (
    <div className="page author-profile-page">
      {authorInfo && <SEO title={authorInfo.name} description={`Découvre les œuvres de ${authorInfo.name} sur Plumly.`} />}
      <button className="btn btn-ghost" onClick={() => navigate(-1)} style={{ marginBottom: 16 }}>
        <IoArrowBack /> Retour
      </button>

      <motion.div
        className="author-profile-header"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
      >
        <div className="author-profile-avatar" style={{
          background: authorInfo?.avatarImage
            ? `url(${authorInfo.avatarImage}) center/cover no-repeat`
            : (authorInfo?.avatarColor || 'linear-gradient(135deg, var(--amber), var(--amber-glow))')
        }}>
          {!authorInfo?.avatarImage && (authorInfo?.name || '?').charAt(0).toUpperCase()}
        </div>

        <h1 className="author-profile-name">{authorInfo.name}</h1>
        {authorInfo.username && <p className="author-profile-username">@{authorInfo.username}</p>}

        <div className="author-profile-badges">
          {accountBadges.map((badge, i) => {
            const Icon = badge.icon;
            return (
              <span key={i} className="author-profile-badge" style={{ background: badge.color + '18', color: badge.color, borderColor: badge.color + '30' }}>
                <Icon /> {badge.label}
              </span>
            );
          })}
        </div>

        {authorInfo.bio && <p className="author-profile-bio">{authorInfo.bio}</p>}

        <div className="author-profile-stats">
          <div className="author-profile-stat">
            <IoBook />
            <span className="author-profile-stat-value">{authorBooks.length}</span>
            <span className="author-profile-stat-label">Livres</span>
          </div>
          <div className="author-profile-stat">
            <IoPeople />
            <span className="author-profile-stat-value">{followerCount}</span>
            <span className="author-profile-stat-label">Followers</span>
          </div>
          <div className="author-profile-stat">
            <IoHeart />
            <span className="author-profile-stat-value">{totalLikes.toLocaleString('fr-FR')}</span>
            <span className="author-profile-stat-label">Likes</span>
          </div>
          <div className="author-profile-stat">
            <IoEye />
            <span className="author-profile-stat-value">{totalViews.toLocaleString('fr-FR')}</span>
            <span className="author-profile-stat-label">Vues</span>
          </div>
        </div>

        {!isOwnProfile && authorUserId && (
          <div className="author-profile-actions">
            <motion.button
              className={'btn ' + (following ? 'btn-secondary' : 'btn-primary')}
              onClick={handleFollow}
              whileTap={{ scale: 0.95 }}
            >
              {following ? <><IoPersonRemove /> Ne plus suivre</> : <><IoPersonAdd /> Suivre</>}
            </motion.button>
            <motion.button
              className="btn btn-accent"
              onClick={() => navigate('/messages')}
              whileTap={{ scale: 0.95 }}
            >
              <IoChatbubble /> Message
            </motion.button>
            <TipAuthor authorName={authorInfo.name} authorId={authorUserId} />
          </div>
        )}

        <div className="author-profile-share">
          <button
            className="btn btn-ghost"
            style={{ fontSize: 12, gap: 6 }}
            onClick={() => {
              navigator.clipboard.writeText(window.location.href);
              alert('Lien du profil copié !');
            }}
          >
            🔗 Copier le lien du profil
          </button>
        </div>
      </motion.div>

      {/* Onglets */}
      <div className="author-profile-tabs">
        <button className={'author-tab ' + (tab === 'livres' ? 'author-tab-active' : '')} onClick={() => setTab('livres')}>
          <IoBook /> Livres ({authorBooks.length})
        </button>
        <button className={'author-tab ' + (tab === 'mur' ? 'author-tab-active' : '')} onClick={() => setTab('mur')}>
          <IoNewspaper /> Mur ({posts.length})
        </button>
        <button className={'author-tab ' + (tab === 'badges' ? 'author-tab-active' : '')} onClick={() => setTab('badges')}>
          <IoTrophy /> Badges ({earnedBadgesDefs.length})
        </button>
      </div>

      {/* TAB LIVRES */}
      <AnimatePresence mode="wait">
        {tab === 'livres' && (
          <motion.div key="livres" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
            <div className="author-profile-books">
              {authorBooks.length > 0 ? (
                authorBooks.map((book, i) => (
                  <motion.div
                    key={book.id}
                    className="author-profile-book"
                    onClick={() => navigate('/book/' + book.id)}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: i * 0.06 }}
                    whileHover={{ y: -3 }}
                  >
                    <div className="author-profile-book-cover" style={{
                      background: book.coverImage
                        ? `url(${book.coverImage}) center/cover no-repeat`
                        : book.coverGradient || 'linear-gradient(135deg, #2a2520, #1a1714)'
                    }}>
                      <div className="author-profile-book-cover-inner">
                        <h3>{book.title}</h3>
                      </div>
                    </div>
                    <div className="author-profile-book-info">
                      <h3 className="author-profile-book-title">{book.title}</h3>
                      <p className="author-profile-book-genre">{book.genre || 'Autre'}</p>
                      <div className="author-profile-book-stats">
                        <span><IoHeart /> {(book.likes || 0).toLocaleString('fr-FR')}</span>
                        <span><IoEye /> {(book.views || 0).toLocaleString('fr-FR')}</span>
                        <span><IoBook /> {book.chapters?.length || 0} chap.</span>
                      </div>
                    </div>
                  </motion.div>
                ))
              ) : (
                <p className="author-profile-empty">Aucun livre publié</p>
              )}
            </div>
          </motion.div>
        )}

        {/* TAB MUR */}
        {tab === 'mur' && (
          <motion.div key="mur" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
            <div className="author-profile-posts">
              {posts.length > 0 ? posts.map((post, i) => (
                <motion.div
                  key={post.id}
                  className="author-post"
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: i * 0.04 }}
                >
                  <div className="author-post-header">
                    <div className="author-post-avatar" style={{
                      background: post.authorAvatar
                        ? `url(${post.authorAvatar}) center/cover no-repeat`
                        : post.authorAvatarColor || 'var(--amber)'
                    }}>
                      {!post.authorAvatar && (post.authorName || '?').charAt(0).toUpperCase()}
                    </div>
                    <div className="author-post-meta">
                      <strong>{post.authorName}</strong>
                      <span>{timeAgo(post.createdAt)}</span>
                    </div>
                  </div>
                  <p className="author-post-text">{post.content}</p>
                  <div className="author-post-actions">
                    <span><IoHeart /> {post.likes || 0}</span>
                    <span><IoChatbubble /> {post.comments || 0}</span>
                  </div>
                </motion.div>
              )) : (
                <div className="author-profile-empty-state">
                  <span>📝</span>
                  <p>Aucune publication pour le moment</p>
                </div>
              )}
            </div>
          </motion.div>
        )}

        {/* TAB BADGES */}
        {tab === 'badges' && (
          <motion.div key="badges" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
            <div className="author-profile-badges-grid">
              {earnedBadgesDefs.length > 0 ? (
                earnedBadgesDefs.map((badge, i) => (
                  <motion.div
                    key={badge.id}
                    className="author-badge-card"
                    initial={{ opacity: 0, scale: 0.8 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ delay: i * 0.06 }}
                  >
                    <span className="author-badge-emoji">{badge.emoji}</span>
                    <h4 className="author-badge-name">{badge.name}</h4>
                    <p className="author-badge-desc">{badge.description}</p>
                  </motion.div>
                ))
              ) : (
                <div className="author-profile-empty-state">
                  <span>🏆</span>
                  <p>Aucun badge débloqué pour le moment</p>
                </div>
              )}
            </div>

            {/* Badges verrouillés */}
            {BADGE_DEFINITIONS.filter(b => !authorBadges.includes(b.id)).length > 0 && (
              <>
                <h4 className="author-badges-locked-title">À débloquer</h4>
                <div className="author-profile-badges-grid">
                  {BADGE_DEFINITIONS.filter(b => !authorBadges.includes(b.id)).map((badge, i) => (
                    <motion.div
                      key={badge.id}
                      className="author-badge-card author-badge-locked"
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      transition={{ delay: i * 0.04 }}
                    >
                      <span className="author-badge-emoji">🔒</span>
                      <h4 className="author-badge-name">{badge.name}</h4>
                      <p className="author-badge-desc">{badge.description}</p>
                    </motion.div>
                  ))}
                </div>
              </>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}